import { api as mondayApi } from './lib/mondayApi';

/**
 * Single upsert used by the template editors (POST /templates/save).
 * payload: { id?, name, access_level, type?, columns, subitems, content? }
 *   type     'subitem' (default) or 'group'
 *   content  the group template body; ignored for subitem templates
 * The backend validates the payload against lib/templateLimits and answers
 * 400 { error } with the reason when it is over a limit.
 * Replaces newTemplate / updateTemplate / updateTemplateMetadata / reorderTemplate,
 * which stay below until their last callers are removed.
 */
const saveTemplate = async (sessionToken, payload) => {
	let res = await fetch('api/templates/save/' + sessionToken, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(payload),
	});

	return await res.json();
};

const getTemplates = async (sessionToken) => {
	let res = await fetch('api/templates/' + sessionToken);
	return await res.json();
};

const newTemplate = async (sessionToken, name, columns, subitems, accessLevel) => {
	const data = {
		name: name,
		columns: columns,
		subitems: subitems,
		access_level: accessLevel,
	};

	let res = await fetch('api/templates/new/' + sessionToken, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json', // Chỉ định là gửi JSON
		},
		body: JSON.stringify(data), // Chuyển đối tượng thành chuỗi JSON
	});

	return await res.json(); // Đảm bảo là nhận dữ liệu JSON
};

const updateTemplateMetadata = async (sessionToken, id, name, accessLevel) => {
	const data = {
		id: id,
		name: name,
		access_level: accessLevel,
	};

	let res = await fetch('api/templates/update/metadata/' + sessionToken, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json', // Chỉ định là gửi JSON
		},
		body: JSON.stringify(data), // Chuyển đối tượng thành chuỗi JSON
	});

	return await res.json(); // Đảm bảo là nhận dữ liệu JSON
};

const updateTemplate = async (sessionToken, id, columns, subitems) => {
	const data = {
		id: id,
		columns: columns,
		subitems: subitems,
	};

	let res = await fetch('api/templates/update/' + sessionToken, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json', // Chỉ định là gửi JSON
		},
		body: JSON.stringify(data), // Chuyển đối tượng thành chuỗi JSON
	});

	return await res.json(); // Đảm bảo là nhận dữ liệu JSON
};

const reorderTemplate = async (sessionToken, id, subitems) => {
	const data = {
		id: id,
		subitems: subitems,
	};

	let res = await fetch('api/templates/reorder/' + sessionToken, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json', // Chỉ định là gửi JSON
		},
		body: JSON.stringify(data), // Chuyển đối tượng thành chuỗi JSON
	});

	return await res.json(); // Đảm bảo là nhận dữ liệu JSON
};

const deleteTemplate = async (sessionToken, id) => {
	const data = {
		id: id,
	};

	let res = await fetch('api/templates/delete/' + sessionToken, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json', // Chỉ định là gửi JSON
		},
		body: JSON.stringify(data), // Chuyển đối tượng thành chuỗi JSON
	});

	return await res.json(); // Đảm bảo là nhận dữ liệu JSON
};

const templateOperation = async (sessionToken, operation, template_id) => {
	const data = {
		operation: operation,
		template_id: template_id,
	};

	let res = await fetch('api/templates/operation/' + sessionToken, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json', // Chỉ định là gửi JSON
		},
		body: JSON.stringify(data), // Chuyển đối tượng thành chuỗi JSON
	});

	return await res.json(); // Đảm bảo là nhận dữ liệu JSON
};

/**
 * Mirrors NO_VERDICT in spt/src/helpers/trial.ts, for when the check cannot be
 * MADE at all: our own endpoint 500s, a proxy answers with an HTML error page,
 * the body will not parse, or the network drops.
 *
 * checkTrial FAILS CLOSED on a subscription it cannot verify, and this has to
 * agree with it — falling open here would let a manual apply run in exactly the
 * cases the automation path refuses.
 */
const NO_VERDICT = {
	status: false,
	message: 'Could not verify this account\u2019s subscription. Please try again shortly.',
};

/**
 * Subscription verdict for this account: { status, message? }.
 *
 * Always resolves to that shape — never null, never rejects — so callers have one
 * thing to handle. An unverifiable subscription comes back as NO_VERDICT above.
 */
const trialStatus = async (sessionToken, itemId) => {
	try {
		const res = await fetch('api/check_purchase/' + itemId + '/' + sessionToken);

		if (!res.ok) {
			console.error('check_purchase failed with status', res.status);
			return NO_VERDICT;
		}

		// A proxy/error page can return HTML with a 200, so parse defensively.
		const text = await res.text();
		if (text.includes('<html>')) {
			console.error('check_purchase returned HTML instead of JSON');
			return NO_VERDICT;
		}

		const verdict = JSON.parse(text);
		// Parsed, but a body with no boolean status is not a verdict either.
		if (typeof verdict?.status !== 'boolean') {
			console.error('check_purchase returned no status field:', verdict);
			return NO_VERDICT;
		}
		return verdict;
	} catch (error) {
		console.error('check_purchase failed:', error);
		return NO_VERDICT;
	}
};

/**
 * In-platform notification (bell icon).
 *
 * Exactly the four arguments create_notification accepts — no `internal`, which
 * is not in the schema and fails the whole mutation.
 * https://developer.monday.com/api-reference/reference/notifications
 *
 * monday reports GraphQL failures as HTTP 200 + errors[] (lib/mondayApi keeps
 * that envelope), so the response has to be inspected — awaiting the call alone
 * reports success for a mutation that never ran. Resolves true only when monday
 * confirms the notification, and never rejects: a notification nobody received
 * must not take down its caller.
 */
const createNotification = async (userId, itemId, text) => {
	if (typeof text !== 'string' || !text.trim()) {
		console.error('create_notification skipped: no text');
		return false;
	}

	try {
		// JSON.stringify escapes quotes, backslashes and newlines in the message.
		const res = await mondayApi(
			`mutation {
				create_notification(user_id: ${userId}, target_id: ${itemId}, text: ${JSON.stringify(text)}, target_type: Project) {
					text
				}
			}`
		);

		if (res?.errors) {
			console.error('create_notification failed', res.errors);
			return false;
		}
		return Boolean(res?.data?.create_notification);
	} catch (e) {
		console.error('create_notification error', e);
		return false;
	}
};

export { getTemplates, saveTemplate, newTemplate, updateTemplate, deleteTemplate, updateTemplateMetadata, templateOperation, trialStatus, reorderTemplate, createNotification };
